import * as sup from "superstruct";

import { createComposableRefinement } from "@/utils/structs/createComposableRefinement";

import { AsyncValue } from "../components/AsyncInput/helpers/AsyncValue";

/**
 * It only checks if given string contains @ between at least two other
 * characters
 */
export const emailPattern = createComposableRefinement<string, null>(
  "emailPattern",
  (value) => value.length === 0 || /.{1,}[@].{1,}/gi.test(value)
);

export const youtubePattern = createComposableRefinement<string | null, null>(
  "youtubePattern",
  (value) => {
    if (value) {
      const pattern = new RegExp(
        "^((?:https?:)?//)?((?:www|m)\\.)?((?:youtube?\\.com|youtu.be))(/(?:[\\w\\-]+\\?v=|v/)?)([\\w\\-]+)(\\S+)?$",
        "gi"
      );
      return pattern.test(value);
    }
    return true;
  }
);

export const passwordPattern = createComposableRefinement<string, null>(
  "passwordPattern",
  (value) =>
    value.length === 0 ||
    /^(?=.*[0-9])(?=.*[!@#$%^&*()_\-=+,<.>/?\\|'":;{}[\]`~])(?=.*[A-Z])[a-zA-Z0-9!@#$%^&*()_\-=+,<.>/?\\|'":;{}[\]`~]{6,}$/.test(
      value
    )
);

export const notEmpty = createComposableRefinement<string, null>(
  "notEmpty",
  (value) => value !== ""
);

export const notEmptyArray = createComposableRefinement<
  unknown[],
  null | undefined
>("notEmpty", (value) => value.length > 0);

export const isTrue = createComposableRefinement<boolean, null>(
  "isTrue",
  (value) => value === true
);

export const pointsRange = (minPoints: number, maxPoints: number) => {
  if (minPoints > maxPoints) {
    throw new Error(
      `minumum value: ${minPoints} is greater than maximum value: ${maxPoints}`
    );
  }
  return createComposableRefinement<number, null>(
    "inRange",
    (value) => value >= minPoints && value <= maxPoints
  );
};

export const areEqual = (fieldKey: keyof sup.Context["branch"][0]) =>
  createComposableRefinement<string, null>("areEqual", (value, ctx) => {
    // Superstruct doesn't provide any typing for Context
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return value === ctx.branch[0][fieldKey];
  });

export const areNotEqual = (fieldKey: keyof sup.Context["branch"][0]) =>
  createComposableRefinement<string, null>("areNotEqual", (value, ctx) => {
    // Superstruct doesn't provide any typing for Context
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return value !== ctx.branch[0][fieldKey];
  });

export const resolvedAsyncValue = <T, S>(baseStruct: sup.Struct<T, S>) => {
  const structType = "resolvedAsyncValue";
  return sup.define<T>(structType, (value) => {
    if (AsyncValue.is(value) && value.status === "rejected") {
      const errorType = value.errorType || "asyncValidationRejected";

      return {
        type: `${structType}.${errorType}`,
        message: "Async validation failed",
      };
    }

    if (AsyncValue.is(value) && value.status === "pending") {
      return {
        type: `${structType}.asyncValidationPending`,
        message: "Async validation is pending",
      };
    }

    const [error] = baseStruct.validate(value);
    return error || true;
  });
};
